/* Dr.Primal — mobile navigation drawer.
   One toggle button opens one full-screen drawer. The drawer traps focus while open,
   closes on Escape or on a click outside the panel, and always restores focus to the
   button that opened it. Locks background scroll while open. */
(function () {
  'use strict';

  // `summary` is in the list for the menu's dropdown rows (2026-10-13): one standing last
  // in the panel would otherwise sit outside the trap's idea of where the panel ends.
  var FOCUSABLE =
    'a[href], button:not([disabled]), summary, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function focusableWithin(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
    });
  }

  function Drawer(toggle) {
    var id = toggle.getAttribute('aria-controls');
    var drawer = id ? document.getElementById(id) : null;
    if (!drawer) return;

    var panel = drawer.querySelector('[data-dp-menu-panel]');
    if (!panel) return;

    var lastFocused = null;

    function isOpen() {
      return drawer.getAttribute('data-dp-open') === 'true';
    }

    function open() {
      if (isOpen()) return;
      // Safari does not focus a <button> on click, so document.activeElement can be
      // <body>. Fall back to the toggle so focus is always restored somewhere useful.
      lastFocused =
        document.activeElement && document.activeElement !== document.body
          ? document.activeElement
          : toggle;
      drawer.setAttribute('data-dp-open', 'true');
      drawer.removeAttribute('aria-hidden');
      drawer.removeAttribute('inert');
      toggle.setAttribute('aria-expanded', 'true');
      document.documentElement.setAttribute('data-dp-scroll-locked', 'true');
      focusFirst();
    }

    // The panel is visibility:hidden until the open attribute lands, and focus() is
    // rejected on a hidden element. Try after the style recalc, then once more on the
    // next frame before giving up.
    function focusFirst(attempt) {
      var tries = attempt || 0;

      requestAnimationFrame(function () {
        if (!isOpen()) return;
        var targets = focusableWithin(panel);
        if (targets.length) targets[0].focus();
        if (!panel.contains(document.activeElement) && tries < 2) focusFirst(tries + 1);
      });
    }

    function close(restoreFocus) {
      if (!isOpen()) return;
      // Move focus out before hiding, so it never rests inside an inert subtree.
      if (restoreFocus !== false && lastFocused && document.contains(lastFocused)) {
        lastFocused.focus();
      } else if (drawer.contains(document.activeElement)) {
        toggle.focus();
      }
      drawer.setAttribute('data-dp-open', 'false');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('inert', '');
      toggle.setAttribute('aria-expanded', 'false');
      document.documentElement.removeAttribute('data-dp-scroll-locked');
    }

    function onKeydown(event) {
      if (!isOpen()) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        close(true);
        return;
      }

      if (event.key !== 'Tab') return;

      var targets = focusableWithin(panel);
      if (!targets.length) {
        event.preventDefault();
        return;
      }

      var first = targets[0];
      var last = targets[targets.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!panel.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }

    toggle.addEventListener('click', function () {
      if (isOpen()) {
        close(true);
      } else {
        open();
      }
    });

    drawer.addEventListener('click', function (event) {
      // The menu's own cart (2026-10-13): the menu closes and the cart drawer is asked
      // for, with the burger named as its trigger so that closing the cart returns focus
      // to something still on the page rather than to a link inside this hidden menu.
      // On a page with no cart drawer the link is left to go to the cart page.
      if (event.target.closest('[data-dp-menu-cart]')) {
        close(true);
        if (!document.getElementById('dp-cart-drawer')) return;
        event.preventDefault();
        document.dispatchEvent(new CustomEvent('dp:cart:open', { detail: { trigger: toggle } }));
        return;
      }
      if (event.target.closest('[data-dp-menu-close]')) {
        close(true);
        return;
      }
      if (!event.target.closest('[data-dp-menu-panel]')) close(true);
    });

    document.addEventListener('keydown', onKeydown);

    // Crossing the 900px breakpoint upward hands navigation back to the desktop bar.
    var wide = window.matchMedia('(min-width: 901px)');
    var onChange = function (event) {
      if (event.matches) close(false);
    };
    if (typeof wide.addEventListener === 'function') {
      wide.addEventListener('change', onChange);
    } else if (typeof wide.addListener === 'function') {
      wide.addListener(onChange);
    }

    // Start closed and inert regardless of the server-rendered attributes.
    drawer.setAttribute('data-dp-open', 'false');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    toggle.setAttribute('aria-expanded', 'false');
  }

  function init() {
    var toggles = document.querySelectorAll('[data-dp-menu-toggle]');
    Array.prototype.forEach.call(toggles, function (toggle) {
      if (toggle.getAttribute('data-dp-menu-ready') === 'true') return;
      toggle.setAttribute('data-dp-menu-ready', 'true');
      Drawer(toggle);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', init);
})();
